import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { publicApi } from '@/services/publicApi';
import { mapListingToCard } from '@/services/listingMapper';
import { useApiData } from '@/hooks/useApiData';
import type { ListingCard } from '@/services/listingMapper';

interface SimilarListingsProps {
  currentId: number;
  category: string;
  location: string;
  city?: string;
}

function SimilarCardSkeleton() {
  return (
    <div className="rounded-2xl card-dark overflow-hidden animate-pulse">
      <div className="aspect-[4/3] bg-background-200/50" />
      <div className="p-4 space-y-2">
        <div className="h-3 bg-background-200/50 rounded w-1/2" />
        <div className="h-4 bg-background-200/50 rounded w-3/4" />
        <div className="h-3 bg-background-200/50 rounded w-full" />
      </div>
    </div>
  );
}

export default function SimilarListings({ currentId, category, location, city }: SimilarListingsProps) {
  const [listings, setListings] = useState<ListingCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { data: settingsResponse } = useApiData(
    () => publicApi.getSettings(),
    []
  );

  const showPrices = useMemo(() => {
    const features = settingsResponse?.data?.features as Record<string, unknown> | undefined;
    return features?.show_prices !== false;
  }, [settingsResponse]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Extract first meaningful word from location or city for search
    const searchTerm = city || location.split(',')[0].trim().split(' ')[0] || '';

    publicApi
      .getListings({ category, search: searchTerm, per_page: 6, page: 1 })
      .then((res) => {
        if (cancelled) return;
        const cards = res.data
          .filter((l) => l.id !== currentId)
          .map(mapListingToCard)
          .slice(0, 5); // Keep max 5 similar
        setListings(cards);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load similar properties');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentId, category, location, city]);

  // If no similar listings and not loading, don't render the section at all
  if (!loading && !error && listings.length === 0) return null;

  return (
    <section className="py-16 bg-background-100">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-10">
          <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-2">
            Explore More
          </span>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950">
            Similar Properties
          </h2>
          <p className="text-sm text-foreground-500 mt-2 max-w-lg mx-auto">
            Discover more {category.toLowerCase()} land parcels you might be interested in.
          </p>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <SimilarCardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-sm text-foreground-500">{error}</p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
              {listings.map((l) => (
                <div
                  key={l.id}
                  className="group rounded-2xl card-dark overflow-hidden hover:border-primary-500/50 transition-all duration-300 flex flex-col"
                >
                  <Link to={`/listings/${l.id}`} className="block relative aspect-[4/3] overflow-hidden cursor-pointer">
                    <img
                      src={l.images[0] || "https://readdy.ai/api/search-image?query=Modern%20minimal%20real%20estate%20placeholder%20image%2C%20abstract%20geometric%20land%20parcel%20shape%2C%20soft%20gradient%20neutral%20background%2C%20warm%20cream%20and%20light%20gold%20tones%2C%20subtle%20grid%20lines%2C%20clean%20professional%20real%20estate%20marketing%20aesthetic&width=800&height=600&seq=listing-fallback&orientation=landscape"}
                      alt={l.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${l.statusColor}`}>
                        {l.status}
                      </span>
                    </div>
                    <div className="absolute top-2 right-2">
                      <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-background-950/60 backdrop-blur-sm text-background-50">
                        {l.category}
                      </span>
                    </div>
                  </Link>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="flex items-center gap-1.5 text-xs text-foreground-500 mb-2">
                      <i className="ri-map-pin-line text-primary-500 text-xs" />
                      <span className="truncate">{l.location}</span>
                    </div>
                    <h3 className="font-display font-semibold text-sm text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                      <Link to={`/listings/${l.id}`}>{l.title}</Link>
                    </h3>
                    <div className="grid grid-cols-2 gap-2 mt-auto">
                      <div className="p-2.5 rounded-lg bg-background-200/50 border border-primary-800/20">
                        <div className="text-xs text-foreground-500 mb-0.5">Size</div>
                        <div className="text-xs font-semibold text-foreground-950">{l.sizeDisplay}</div>
                      </div>
                      {showPrices ? (
                        <div className="p-2.5 rounded-lg bg-background-200/50 border border-primary-800/20">
                          <div className="text-xs text-foreground-500 mb-0.5">Rent</div>
                          <div className="text-xs font-semibold text-primary-600">{l.expectedRentDisplay}</div>
                        </div>
                      ) : (
                        <div className="p-2.5 rounded-lg bg-background-200/50 border border-primary-800/20">
                          <div className="text-xs text-foreground-500 mb-0.5">Price</div>
                          <div className="text-xs font-semibold text-foreground-400">Contact for price</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/listings"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-primary-700/50 text-primary-400 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap"
              >
                View All Listings <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}